import useOverlay from '@/hooks/useOverlay'
import { ProjectTag } from '@/types/project-tags'
import { ContentCopy, Delete, Edit, MoreVert } from '@mui/icons-material'
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import { useGetDocProjectTags } from '@/service-library/hooks/doc-project-tags'
import { useDeleteProjectTag } from '@/service-library/hooks/project-tags'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import {
  showErrorSnackbar,
  showInfoSnackbar,
  showSuccessSnackbar,
} from '@/utils/snackbars'
import { useDemoModeContext } from '@/components/demo-mode-provider/DemoModeProvider'
import copyToClipboard from '@/components/document-actions-menu/copy-to-clipboard'
import AddEditTagDialog from './AddEditTagDialog'
import TagDeleteConfirmationDialog from './TagDeleteConfirmationDialog'

type TagActionsMenuProps = {
  projectTag: ProjectTag
  listQueryKey: QueryKey
}

export default function TagActionsMenu({
  projectTag,
  listQueryKey,
}: TagActionsMenuProps) {
  const overlay = useOverlay()
  const editOverlay = useOverlay()
  const deleteConfirmationOverlay = useOverlay()
  const [demoMode] = useDemoModeContext()
  const isSuperUser = useIsSuperUser()

  const { data } = useGetDocProjectTags({
    paginator: 'offset',
    filters: {
      limit: '1',
      project_tag_id: projectTag.id,
      fields__only: 'id',
    },
  })
  const tagCount = data?.pages[0].count || 0

  const { deleteProjectTag } = useDeleteProjectTag({
    listQueryKey,
    onSuccess: () => {
      showSuccessSnackbar('Tag deleted successfully')
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to delete tag. Please contact support if this error continues.',
      )
    },
  })

  return (
    <>
      <IconButton onClick={overlay.open} size="small">
        <MoreVert />
      </IconButton>
      <Menu
        id="tag-menu"
        anchorEl={overlay.anchorEl as Element}
        open={overlay.isOpen}
        onClose={overlay.close}
      >
        <MenuItem
          onClick={(e) => {
            editOverlay.open(e)
            overlay.close()
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            if (tagCount > 0) {
              deleteConfirmationOverlay.open(e)
            } else {
              deleteProjectTag(projectTag.id)
            }
            overlay.close()
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <Typography component="span" color="error">
            Delete
          </Typography>
        </MenuItem>
        {isSuperUser && !demoMode && (
          <MenuItem
            key="copy-id"
            onClick={() => {
              copyToClipboard(projectTag.id)
              showInfoSnackbar('Copied to Clipboard')
              overlay.close()
            }}
            sx={{ color: SUPER_USER_ONLY_COLOR }}
          >
            <ListItemIcon sx={{ color: SUPER_USER_ONLY_COLOR }}>
              <ContentCopy fontSize="small" />
            </ListItemIcon>
            Copy ID
          </MenuItem>
        )}
      </Menu>

      <AddEditTagDialog
        overlay={editOverlay}
        projectTag={projectTag}
        listQueryKey={listQueryKey}
      />
      <TagDeleteConfirmationDialog
        overlay={deleteConfirmationOverlay}
        projectTag={projectTag}
        count={tagCount}
        onDelete={() => {
          deleteProjectTag(projectTag.id)
        }}
      />
    </>
  )
}
